import { useSWService } from 'src/api/sw/useSWService';
import { useAppStore } from 'src/context/AppContext';
import { AppModalData } from 'src/types/ModalData';

const useTriggerReleaseVersionCheck = () => {
    const { appRefreshPostponed, setAppRefreshPostponed, setAppModalData } = useAppStore();
    const { clearCache } = useSWService();

    const refreshTitle = 'A new version of OrcaWeb is available!';
    const refreshMessage =
        'Please refresh. Continuing on this version might cause errors. If you have unsaved work, select Cancel, save your work and refresh the website as soon as possible.';
    const refreshActionButtonText = 'Refresh';

    const updateInProgressTitle = 'System Update in Progress';
    const updateInProgressMessage =
        'OrcaWeb is currently being updated. The application may be unresponsive for up to 15 minutes. Please wait for the update to complete before continuing.';
    const updateInProgressActionButtonText = 'OK';

    /**
     * Compare two version strings to determine if version1 is newer than version2
     * Handles semantic versioning (x.y.z) and build numbers
     * @param version1 - First version to compare
     * @param version2 - Second version to compare
     * @returns true if version1 is newer than version2
     */
    const isVersionNewer = (version1: string, version2: string): boolean => {
        if (!version1 || !version2) return false;

        // Handle case where versions are identical
        if (version1 === version2) return false;

        // Simple string comparison for build IDs or timestamps
        // If they contain only numbers, treat as build numbers
        if (/^\d+$/.test(version1) && /^\d+$/.test(version2)) {
            return parseInt(version1, 10) > parseInt(version2, 10);
        }

        // For semantic versioning or other formats, use string comparison as fallback
        // This could be enhanced based on the actual version format used
        return version1 > version2;
    };

    const refresh = () => {
        // Clear postponed version tracking since we're refreshing
        sessionStorage.removeItem('postponedVersionId');
        // Clear update modal tracking as well
        sessionStorage.removeItem('lastUpdateModalTime');
        clearCache();
        window.location.reload();
    };

    const cancelRefresh = (versionId?: string) => {
        const APP_REFRESH_POSTPONE_MINUTES = 10;
        const now = new Date();
        const delayToTime = new Date(now.setMinutes(now.getMinutes() + APP_REFRESH_POSTPONE_MINUTES));
        setAppRefreshPostponed(delayToTime);

        // Store the version that was postponed to avoid showing modal again for same version
        if (versionId) {
            sessionStorage.setItem('postponedVersionId', versionId);
        }
    };

    const triggerModal = (releaseIdFromApi: string) => {
        const _appModalData: AppModalData = {
            title: refreshTitle,
            message: refreshMessage,
            actionButtonText: refreshActionButtonText,
            action: refresh,
            cancel: () => cancelRefresh(releaseIdFromApi),
        };
        setAppModalData({ ..._appModalData });
    };

    const triggerUpdateInProgressModal = () => {
        const _appModalData: AppModalData = {
            title: updateInProgressTitle,
            message: updateInProgressMessage,
            actionButtonText: updateInProgressActionButtonText,
            action: () => {}, // Just close the modal, no action needed
            // No cancel option for this modal - user just needs to acknowledge
        };
        setAppModalData({ ..._appModalData });
    };

    const releaseVersionCheck = (releaseIdFromApi, appSettingsReleaseId) => {
        // compares releaseId from API with current displayed website version,
        // if mismatch, setAppRequiresRefresh which should trigger a popup in RouterContent.tsx
        // User can postpone this popup by clicking cancel
        if (!releaseIdFromApi || releaseIdFromApi?.length < 1) return;
        if (releaseIdFromApi === appSettingsReleaseId) {
            // Versions match - clear any update modal tracking since update is complete
            sessionStorage.removeItem('lastUpdateModalTime');
            return;
        }
        if (appSettingsReleaseId === 'localhost') return;

        // Handle case where frontend version is newer than API version (deployment in progress)
        if (isVersionNewer(appSettingsReleaseId, releaseIdFromApi)) {
            // Frontend is newer - likely a deployment in progress
            console.log('Frontend version is newer than API version - deployment in progress');

            // Check if we already showed the update-in-progress modal recently to avoid spam
            const lastUpdateModalTime = sessionStorage.getItem('lastUpdateModalTime');
            const UPDATE_MODAL_COOLDOWN_MINUTES = 5;
            const now = new Date().getTime();

            if (!lastUpdateModalTime || now - parseInt(lastUpdateModalTime, 10) > UPDATE_MODAL_COOLDOWN_MINUTES * 60 * 1000) {
                // Show the update-in-progress modal and record the time
                sessionStorage.setItem('lastUpdateModalTime', now.toString());
                triggerUpdateInProgressModal();
            }

            return;
        }

        // Check if this version was already postponed to avoid repeated pop-ups
        const postponedVersionId = sessionStorage.getItem('postponedVersionId');
        if (postponedVersionId === releaseIdFromApi && appRefreshPostponed && appRefreshPostponed.getTime() > new Date().getTime()) {
            // Same version is still postponed, don't show modal again
            return;
        }

        // Only trigger modal if not postponed or postpone period has expired
        if (!appRefreshPostponed) {
            triggerModal(releaseIdFromApi);
            return;
        }
        if (appRefreshPostponed.getTime() < new Date().getTime()) {
            // Clear the postponed version when postpone period expires
            sessionStorage.removeItem('postponedVersionId');
            triggerModal(releaseIdFromApi);
        }
    };

    return { releaseVersionCheck };
};

export default useTriggerReleaseVersionCheck;
